import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { TRADE_ID_PATTERN } from 'utils/regexp';
import useReturnToAppInterstitial from 'hooks/useReturnToAppInterstitial';

/**
 * PaymentSuccessPage — thin immediate redirector.
 *
 * Extracts `tradeId` from the URL search params, validates it is numeric,
 * then immediately navigates to the trade details page with `?fromPayment=success`.
 * If `tradeId` is absent or non-numeric, shows a warning snackbar and redirects
 * to the trades list instead.
 *
 * When the page loads in an external mobile browser (after a payment gateway
 * redirect), an interstitial is shown first to let the user return to the
 * native app. The deep link points directly to `/trades/{tradeId}?fromPayment=success`
 * so the user lands on the trade details page inside the app, not on this
 * redirector. Navigation only proceeds after the user dismisses the interstitial.
 *
 * A ref guard ensures the one-shot effect runs exactly once even under React
 * StrictMode's double-invocation in development — without it the warning
 * snackbar and navigate call would both fire twice.
 */
export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasRun = useRef(false);

  // build the deep-link target path pointing at the trade details page
  const tradeId = searchParams.get('tradeId');
  const targetPath =
    tradeId !== null && TRADE_ID_PATTERN.test(tradeId) ? `/trades/${tradeId}?fromPayment=success` : undefined;

  const { active: interstitialActive, element: interstitialElement } = useReturnToAppInterstitial(
    'success',
    targetPath,
  );

  useEffect(() => {
    if (hasRun.current) return;

    // block navigation while the interstitial is visible
    if (interstitialActive) return;

    hasRun.current = true;

    // redirect to trade details when tradeId is valid, otherwise fall back to trades list
    if (targetPath) {
      navigate(targetPath, { replace: true });
    } else {
      // log the pathname + the SET of param keys the gateway returned, so operators
      // can tell which params came back (e.g. "token, PayerID" with no tradeId — proof
      // the backend's PayPal return URL is missing the tradeId query parameter).
      // Values are deliberately not logged to avoid surfacing gateway tokens.
      if (typeof window !== 'undefined') {
        const paramKeys = Array.from(new URLSearchParams(window.location.search).keys()).join(', ') || '(none)';
        console.warn(
          `[PaymentSuccess] Missing or invalid tradeId on ${window.location.pathname}. Params: ${paramKeys}`,
        );
      }
      enqueueSnackbar('Invalid or missing trade ID. Redirecting to your trades.', { variant: 'warning' });
      navigate('/trades', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interstitialActive]);

  // show interstitial when in external mobile browser
  if (interstitialElement) return interstitialElement;

  return null;
}
